/**
 * Registry adapters for InvestmentProjector, InvestmentGrowth, SafeWithdrawal and the goal
 * functions. Same rules as registry.ts: decode, call, encode; output shapes mirror
 * ContractRegistry.swift.
 */
import { formatDay } from './core/calendar.ts';
import {
  calculateCurrentValue, calculateTotalCurrentInvestments, createInvestmentTransactionForecast, projectInvestments,
} from './calculations/investment-projector.ts';
import {
  annuityFutureValue, contributionBalances, contributionFutureValue, lumpSumFutureValue, yearsOfGrowthEquivalent,
} from './calculations/investment-growth.ts';
import { safeWithdrawalMonthlyIncome } from './calculations/safe-withdrawal.ts';
import { decodeBalances, decodeGoal, decodeSeries, decodeTransaction, decodeTransactions, decodeWindow } from './codec/entities.ts';
import {
  contextFrom, decodeDay, decodeDecimal, encodeDecimal, encodeDouble, encodeOptionalDay, optionalDay, optionalDecimal,
} from './codec/scalars.ts';
import { goalDateMatchProgress, isMatchAchieved, monthsUntilMatch } from './goals/goal-date-match.ts';
import { calculateGoalProgress, findGoalMatch, type GoalForecast, type GoalProgress } from './goals/goal-tracker.ts';
import { generateGoalSuggestions, suggestNextGoal, type SuggestedGoal } from './goals/smart-goal-suggester.ts';
import type { Registry } from './registry.ts';
import type * as Wire from './types/contract.generated.ts';

const decodeGoals = (goals: readonly Wire.Goal[] | null | undefined) => (goals ?? []).map(decodeGoal);

const decodeGoalForecast = (f: Wire.CumulativeInput | null | undefined): GoalForecast => ({
  cash: decodeSeries(f?.cash),
  investment: decodeSeries(f?.investment),
  debt: decodeSeries(f?.debt),
  netWorth: decodeSeries(f?.netWorth),
  passive: decodeSeries(f?.passive),
  debtFreeDate: optionalDay(f?.debtFreeDate),
});

export const encodeGoalProgress = (p: GoalProgress): Wire.GoalProgress => ({
  id: p.goal.id,
  currentValue: encodeDecimal(p.currentValue),
  targetValue: encodeDecimal(p.targetValue),
  progressPercent: encodeDouble(p.progressPercent),
  estimatedDate: encodeOptionalDay(p.estimatedDate),
  monthsRemaining: p.monthsRemaining,
  isAchieved: p.isAchieved,
});

const encodeSuggestion = (s: SuggestedGoal): Wire.Suggestion => ({
  series: s.series,
  operation: s.operation,
  targetValue: encodeDecimal(s.targetValue),
  priority: s.priority,
});

export const investmentAndGoalAdapters: Registry = {
  'InvestmentProjector.project': (input) => {
    const buckets = projectInvestments(
      decodeTransactions(input.transactions),
      decodeWindow(input.window),
      optionalDecimal(input.withdrawalRatePercent) ?? undefined,
      optionalDecimal(input.annualReturnPercent) ?? undefined,
      decodeTransactions(input.incomes ?? []),
    );
    return {
      starts: buckets.map((b) => formatDay(b.start)),
      end: buckets.length > 0 ? formatDay(buckets[buckets.length - 1]!.end) : null,
      amounts: buckets.map((b) => encodeDecimal(b.amount)),
      passiveIncome: buckets.map((b) => encodeDecimal(b.passiveIncome)),
    };
  },

  'InvestmentProjector.createInvestmentTransactionForecast': (input) => {
    const f = createInvestmentTransactionForecast(
      decodeTransaction(input.transaction),
      decodeWindow(input.window),
      optionalDecimal(input.annualReturnPercent) ?? undefined,
      decodeTransactions(input.incomes ?? []),
    );
    return {
      transactionForecast: f.transactionForecast.map(encodeDecimal),
      interestForecast: f.interestForecast.map(encodeDecimal),
      interestTotal: encodeDecimal(f.interestTotal),
      total: encodeDecimal(f.total),
    };
  },

  'InvestmentProjector.calculateTotalCurrentInvestments': (input) => ({
    total: encodeDecimal(
      calculateTotalCurrentInvestments(
        decodeTransactions(input.transactions),
        decodeDay(input.date),
        optionalDecimal(input.annualReturnPercent) ?? undefined,
        decodeTransactions(input.incomes ?? []),
      ),
    ),
  }),

  'InvestmentProjector.calculateCurrentValue': (input) => ({
    value: encodeDecimal(
      calculateCurrentValue(
        decodeTransaction(input.transaction),
        decodeDay(input.date),
        optionalDecimal(input.annualReturnPercent) ?? undefined,
        decodeTransactions(input.incomes ?? []),
      ),
    ),
  }),

  'InvestmentGrowth.contributionBalances': (input) => {
    const contribution = decodeDecimal(input.monthlyContribution);
    const rate = decodeDecimal(input.annualRatePercent);
    return {
      balances: contributionBalances(contribution, rate, input.months).map(encodeDecimal),
      futureValue: encodeDecimal(contributionFutureValue(contribution, rate, input.months)),
    };
  },

  'InvestmentGrowth.annuityFutureValue': (input) => ({
    value: encodeDecimal(
      annuityFutureValue(decodeDecimal(input.monthlyPayment), decodeDecimal(input.annualRatePercent), input.months),
    ),
  }),

  'InvestmentGrowth.lumpSumFutureValue': (input) => ({
    value: encodeDecimal(lumpSumFutureValue(decodeDecimal(input.principal), decodeDecimal(input.annualRatePercent), input.years)),
  }),

  'InvestmentGrowth.yearsOfGrowthEquivalent': (input) => ({
    years: encodeDouble(
      yearsOfGrowthEquivalent(decodeDecimal(input.amount), decodeDecimal(input.base), decodeDecimal(input.annualRatePercent)),
    ),
  }),

  'SafeWithdrawal.monthlyIncome': (input) => ({
    monthlyIncome: encodeDecimal(
      safeWithdrawalMonthlyIncome(decodeDecimal(input.balance), optionalDecimal(input.ratePercent) ?? undefined),
    ),
  }),

  'GoalTracker.findMatch': (input) => {
    const match = findGoalMatch(decodeGoal(input.goal), decodeSeries(input.payments), contextFrom(input));
    return { date: encodeOptionalDay(match.date), currentValue: encodeDecimal(match.currentValue) };
  },

  'GoalTracker.calculateProgress': (input) =>
    calculateGoalProgress(decodeGoals(input.goals), decodeGoalForecast(input.forecast), contextFrom(input), {
      currentBalances: input.currentBalances === undefined || input.currentBalances === null ? null : decodeBalances(input.currentBalances),
      baselines: {
        maxDebt: optionalDecimal(input.historicalBaselines?.maxDebt),
        maxNetWorth: optionalDecimal(input.historicalBaselines?.maxNetWorth),
      },
    }).map(encodeGoalProgress),

  'GoalDateMatch.progress': (input) => {
    const goal = decodeGoal(input.goal);
    const date = optionalDay(input.date);
    return {
      progress: encodeDouble(goalDateMatchProgress(goal, decodeDecimal(input.currentValue))),
      isAchieved: isMatchAchieved({ date }),
      estimatedMonthsRemaining: monthsUntilMatch(date, contextFrom(input).now),
    };
  },

  'SmartGoalSuggester.generateSuggestions': (input) =>
    generateGoalSuggestions({
      currentNetWorth: decodeDecimal(input.currentNetWorth),
      currentDebt: decodeDecimal(input.currentDebt),
      currentCash: decodeDecimal(input.currentCash),
      monthlyExpenses: optionalDecimal(input.monthlyExpenses),
      existingGoals: decodeGoals(input.existingGoals),
      includeStretchGoals: input.includeStretchGoals ?? false,
    }).map(encodeSuggestion),

  'SmartGoalSuggester.suggestNextGoal': (input) => {
    const suggestion = suggestNextGoal(decodeGoal(input.achievedGoal), decodeDecimal(input.currentNetWorth));
    return { suggestion: suggestion === null ? null : encodeSuggestion(suggestion) };
  },
};
